import { type EventMap, Events } from "@/app/api"
import type { AppConfig } from "@/config"
import Styles from "./styles.css?inline"
import { ShadowRealm, Vite, attrs2props } from "./utils"

const liqa = Vite.dependencies(() => import("@/root"))
const errors = Vite.dependencies(() => import("@/libs/telemetry/errors"))

/**
 * The Web component name.
 * @category Misc
 */
export const NAME = "hautai-liqa" as const

type Sources =
  | "front_camera"
  | "back_camera"
  | "upload"
  | "companion"
  | "front_camera,back_camera"
  | "front_camera,upload"
  | "front_camera,upload,companion"
  | "front_camera,companion"
  | "front_camera,back_camera,upload"
  | "front_camera,back_camera,companion"
  | "front_camera,back_camera,upload,companion"
  | "back_camera,upload"
  | "back_camera,companion"
  | "back_camera,upload,companion"
  | "upload,companion"

/**
 * LIQA Web component HTML attributes to configure the component look and behavior.
 * @example
 * ```html
 * <hautai-liqa
 *   license="xxx-xxx-xxx"
 *   styles="@import url('/path/to/custom-liqa-styles.css')"
 * >
 * </hautai-liqa>
 * ```
 * @category Component
 */
export type LiqaAttributes = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }. */
  license: string
  /**
   * A string containing valid CSS rules. For more information, see [Styles](/customization/ui/styles) page.
   * @example
   * "@import url('/path/to/custom-liqa-styles.css')"
   */
  styles?: string
  /**
   * A string containing a valid JSON object whose keys are messages keys and values are messages texts. For more information, see [Text Messages](/customization/ui/text-messages) page.
   * @example
   * "{ \"preview\": { \"submit\": \"Confirm\" } }"
   */
  messages?: string
  /**
   * A preset used to customize AI quality criteria and user flow. For more information, see [Presets](customization/user-flow/presets) page.
   * @default "face"
   */
  preset?: "face" | "face-180" | "hair"
  /**
   * A list of sources to capture the image from: different device cameras or file upload. They can be combined using `,` separator.
   * Visit the corresponding [user flow customization](/customization/user-flow/image-sources) section to learn about their logic and limitations.
   *
   * @default "front_camera"
   * @alias `"camera"`
   * Starting from `6.7.0` version `"camera"` acts as an alias for `"front_camera"`.
   *
   * _Note: For_ `face-180` _preset only_ `"front_camera"` _source is available. The rest will be ignored._
   */
  sources?: Sources
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   *
   * _Note: The option is only applicable to_ `"hair"` _preset. For other presets it is always_ `"auto"`_._
   */
  capture?: "auto" | "manual"
  /**
   * Lighting requirements for the image capture.
   * - `"none"`: no requirements are set and lighting will not be validated. (**WARNING**: the quality of the processing results may be affected)
   * - `"adaptive"`: the requirements will be softened if the lighting conditions are poor.
   * - `"default"`: the requirements will be set to optimal which will provide more details on the photo.
   *
   * _Note: The option is only applicable to_ `"face"` _preset. For other presets it is always_ `"default"`_._
   *
   * @default `"default"`
   */
  "required-lighting"?: "none" | "adaptive" | "default"
  /**
   * Image post-processing settings.
   * - `"original"`: the image from `"capture"` will not be processed.
   * - `"anonymized"`: the image from `"capture"` will be anonymized. (only skin mask will be returned)
   *
   * @default `"original"`
   */
  postprocessing?: "original" | "anonymized"
  /**
   * A flag to enable or disable the effects during the image capture process.
   *
   * @default false
   */
  effects?: boolean | "true" | "false"
  /**
   * A flag to enable or disable tutorial option on the source selection screen.
   * Enabled only when sources option contain "front_camera" or "back_camera" and "upload".
   *
   * - `"video"`: a video tutorial will be shown.
   * - `"image"`: an image tutorial will be shown. Can be used as a fallback for reducing the loading time.
   *
   * @default "video"
   */
  tutorial?: string | "image" | "video"
  /**
   * A flag to set the entry point of the LIQA.
   *
   * - `"capture"`: the entry point will be set to the capture screen.
   * - `"tutorial"`: the entry point will be set to the tutorial screen.
   *
   * @default "capture"
   */
  "entry-point"?: string | "capture" | "tutorial"
  /**
   * A flag to set the quality check behavior.
   *
   * - `off`: the quality check will be disabled.
   * - `strict`: the quality check will be strict and the user will not be able to continue if the quality criteria are not met.
   * - `normal`: the quality check will be normal and the user will be able to continue even if the quality criteria are not met.
   *
   * @default `"off"`
   */
  "quality-check"?: "off" | "normal" | "strict"
  /**
   * An array specifying which capture sources should enforce quality checks.
   *
   * The quality check behavior (set via the `qualityCheck` property) will only be applied for the sources included in this array.
   * Available values are:
   * - `front_camera`: Enforces quality check on images captured from the front camera.
   * - `back_camera`: Enforces quality check on images captured from the back camera.
   * - `upload`: Enforces quality check on images provided via file upload.
   * - `companion`: Enforces quality check on images captured via a companion source.
   *
   * If not provided, quality checks will be applied for all sources.
   *
   * @default ["front_camera","back_camera","upload","companion"]
   */
  "quality-check-sources"?: Sources
  /**
   * A flag to enable or disable best frame selection during image capture.
   *
   * When enabled, LIQA will track multiple frames during the capture process
   * and automatically select the frame with the highest quality metrics.
   *
   * @default `"true"`
   */
  "best-frame-selection"?: "true" | "false"
}

/**
 * The component event names and corresponding event details.
 * @example
 * ```html
 * <hautai-liqa
 *   license="xxx-xxx-xxx"
 *   onready="console.log('LIQA is ready!')"
 *   onerror="console.warn('An error occurred:', event.detail)"
 * >
 * </hautai-liqa>
 * ```
 * @category Component
 */
export type {
  AnalyticsDetails,
  AnalyticsEventsMap,
  CaptureFormat,
  CaptureMetadata,
  CaptureTransformations,
  ErrorCodes,
  ImageCapture,
  LiqaError,
} from "@/root"
export type { EventMap as LiqaEventMap }

// TODO: document all the black magic going here and particularly why it's implemented as it's implemented

/**
 * LIQA Web component available as `<hautai-liqa></hautai-liqa>` tag.
 * @example
 * ```html
 * <hautai-liqa license="xxx-xxx-xxx"></hautai-liqa>
 * ```
 * @category Component
 */
export class LiqaElement extends HTMLElement {
  readonly #root: ShadowRoot
  #widget: Promise<{
    component: ReturnType<typeof import("@/root").create>
    globalErrorTracker: { destroy: () => void }
  }> | null

  get #isE2e() {
    return typeof __LIQA_E2E__ !== "undefined" && __LIQA_E2E__
  }

  /** @hidden */
  constructor() {
    super()

    this.#root = this.attachShadow({ mode: this.#isE2e ? "open" : "closed" })
    this.#widget = null

    // new CSSStyleSheet() API is not available on Safari < 16.4
    const style = document.createElement("style")
    style.innerHTML = Styles
    this.#root.appendChild(style)
  }

  protected connectedCallback() {
    // https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#using_the_lifecycle_callbacks
    if (!this.isConnected) return

    if (this.#widget) return

    // Isolated JavaScript execution context
    const realm = new ShadowRealm(this.#root)

    if (this.#isE2e)
      realm["_getGlobal"]().then((realm) => {
        // use global getUserMedia instead of the isolated one from the Realm to allow test environment to mock it
        Object.getPrototypeOf(realm.navigator.mediaDevices).getUserMedia = Object.getPrototypeOf(
          self.navigator.mediaDevices,
        ).getUserMedia
      })

    this.#widget = Promise.resolve()
      .then(async () => ({
        // enable global error tracking
        destroy: (
          await realm.importValue<typeof import("@/libs/telemetry/errors").setup>(
            Vite.resolve(errors.js),
            "setup",
          )
        )(),
      }))
      .then(async (globalErrorTracker) => {
        // Inject the dynamically imported by Vite CSS styles into the shadowRoot instead of the Realm‘s document.head
        // https://github.com/vitejs/vite/issues/11855#issuecomment-1762258886
        realm["_getGlobal"]().then((realm) => Vite.adoptStyles(realm.document.head, this.#root))

        const create = await Vite.preload(
          () => realm.importValue<typeof import("@/root").create>(Vite.resolve(liqa.js), "create"),
          liqa.css,
          import.meta.url,
          this.#root,
        )

        const attrs = attrs2props<LiqaAttributes>(this.attributes)
        const props: AppConfig = {
          ...attrs,
          effects: !!attrs.effects && String(attrs.effects) !== "false",
          settings: (attrs as any).settings ? JSON.parse((attrs as any).settings) : undefined,
          messages: attrs.messages ? JSON.parse(attrs.messages) : undefined,
          sources: attrs.sources?.split(",").map((s) => s.trim()) as AppConfig["sources"],
          qualityCheckSources: attrs["quality-check-sources"]
            ?.split(",")
            .map((s) => s.trim()) as AppConfig["qualityCheckSources"],
        }

        return { component: create(this.#root, props), globalErrorTracker }
      })
      .catch((error) => {
        // track any errors during the component loading and initialization
        const license = this.attributes.getNamedItem("license")?.value

        realm
          .importValue(Vite.resolve(error.js), "log")
          .then((log) => log(error, { tags: { license } }))

        throw error
      })
  }

  protected disconnectedCallback() {
    if (!this.#widget) return

    const children = new Set(this.#root.children)

    this.#widget.then(({ component, globalErrorTracker }) => {
      globalErrorTracker.destroy()
      component?.destroy()

      for (const child of this.#root.children) {
        if (children.has(child)) this.#root.removeChild(child)
      }
    })
    this.#widget = null
  }

  /**
   * Programmatically uploads one or more files to LIQA, bypassing the manual file selection UI.
   * @param files - An array of File objects to upload.
   * @example
   * ```js
   * // Upload a single file
   * const fileInput = document.querySelector('#file-input')
   * const files = Array.from(fileInput.files)
   * liqa.upload(files)
   *
   * // Upload multiple files programmatically
   * const files = [file1, file2, file3]
   * liqa.upload(files)
   * ```
   */
  protected upload(files: File[]): void {
    this.#widget?.then(({ component }) => component?.upload(files))
  }

  /**
   * Programmatically resets LIQA to its initial state, clearing any captured images and stopping the camera.
   * @example
   * ```js
   * // Reset LIQA to start over
   * liqa.reset()
   * ```
   */
  protected reset(): void {
    this.#widget?.then(({ component }) => component?.reset())
  }

  /** @internal */
  override addEventListener(...args: unknown[]): void {
    if (args[0] === Events.LOADED || args[0] === Events.CAPTURE) {
      console.warn(
        `[LIQA] Deprecation warning: the "${args[0]}" event is deprecated. Subscribe to "${args[0] === Events.LOADED ? Events.READY : Events.CAPTURES}" event instead.`,
      )
    }

    super.addEventListener(...(args as [string, EventListenerOrEventListenerObject]))
  }
}

export interface LiqaElement {
  /** Subscribes the event listener to the given LIQA {@link EventMap event}. */
  addEventListener<K extends keyof EventMap>(
    type: K,
    listener: (this: LiqaElement, ev: CustomEvent<EventMap[K]>) => any,
    options?: boolean | AddEventListenerOptions,
  ): void
  /** @hidden */
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void
  /** Unsubscribes the event listener from the given LIQA {@link EventMap event}. */
  removeEventListener<K extends keyof EventMap>(
    type: K,
    listener: (this: LiqaElement, ev: CustomEvent<EventMap[K]>) => any,
    options?: boolean | EventListenerOptions,
  ): void
  /** @hidden */
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void
}
